import { db } from '../db';
import { base64ToBlob, blobToBase64, photoForStorage } from '../lib/image';
import { MAX_SYNC_PHOTO_BASE64_LENGTH } from '../lib/item-values';
import { normalizeRemoteItem } from '../lib/remote-item';
import { getSyncSince } from '../lib/sync-cursor';
import type { ItemRecord, OutboxEvent, SyncSummary } from '../types';
import { getLastSyncAt, getSyncConfig, saveLastSyncAt } from './settings';

type PushResponse = { accepted?: string[]; error?: string };
type PullResponse = { items?: unknown[]; serverTime?: string; cursor?: string; error?: string };

let syncQueue: Promise<unknown> = Promise.resolve();
const REQUEST_TIMEOUT_MS = 20_000;
const MAX_PUSH_BATCHES = 20;
const MAX_PUSH_BODY_BYTES = 1_900_000;
const MAX_PULL_PAGES = 1_000;
const textEncoder = new TextEncoder();
type PushEvent = Omit<OutboxEvent, 'payload'> & {
  payload: OutboxEvent['payload'] & { photoBase64?: string; photoMimeType?: string };
};

export function syncNow(): Promise<SyncSummary> {
  const next = syncQueue.then(runSync, runSync);
  syncQueue = next.catch(() => undefined);
  return next;
}

async function runSync(): Promise<SyncSummary> {
  const { endpoint, token } = await getSyncConfig();
  if (!endpoint || !token) return { pushed: 0, pulled: 0, message: 'Cloud sync is not configured.' };

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  let pushed = 0;
  const skippedPhotoIds = new Set<string>();

  for (let batch = 0; batch < MAX_PUSH_BATCHES; batch += 1) {
    const events = await db.outbox.orderBy('createdAt').limit(25).toArray();
    if (!events.length) break;
    const batchResult = await hydratePushBatch(events);
    const hydrated = batchResult.events;
    batchResult.skippedPhotoIds.forEach((id) => skippedPhotoIds.add(id));

    if (!hydrated.length) break;
    const json = await fetchJson<PushResponse>(`${endpoint}/push`, { method: 'POST', headers, body: JSON.stringify({ events: hydrated }) }, 'Sync push failed');
    const accepted = new Set(json.accepted ?? []);
    if (!accepted.size) break;
    pushed += accepted.size;
    await db.transaction('rw', db.items, db.outbox, async () => {
      const acceptedEvents = [];
      for (const event of events) {
        if (accepted.has(event.id)) acceptedEvents.push(event);
      }
      await Promise.all(acceptedEvents.map(async (event) => {
        const current = await db.outbox.get(event.itemId);
        if (current?.id !== event.id) return;
        await Promise.all([
          db.outbox.delete(event.itemId),
          db.items.update(event.itemId, { pendingSync: false })
        ]);
      }));
    });
  }

  const [lastSyncAt, localCount] = await Promise.all([getLastSyncAt(), db.items.count()]);
  const since = getSyncSince(lastSyncAt, localCount);
  let cursor = '';
  let pulled = 0;
  let serverTime = '';
  for (let page = 0; page < MAX_PULL_PAGES; page += 1) {
    const params = new URLSearchParams({ since, paged: '1' });
    if (cursor) params.set('cursor', cursor);
    const json = await fetchJson<PullResponse>(`${endpoint}/pull?${params}`, { headers }, 'Sync pull failed');
    pulled += await applyRemoteItems(json.items ?? []);
    if (!serverTime && json.serverTime && Number.isFinite(Date.parse(json.serverTime))) serverTime = json.serverTime;
    cursor = json.cursor || '';
    if (!cursor) break;
    if (page === MAX_PULL_PAGES - 1) throw new Error('Sync pull returned too many pages.');
  }

  if (serverTime) await saveLastSyncAt(serverTime);
  if (skippedPhotoIds.size) {
    return {
      pushed,
      pulled,
      message: `${skippedPhotoIds.size} local photo${skippedPhotoIds.size === 1 ? '' : 's'} could not be prepared. Other changes were synced.`
    };
  }
  return {
    pushed,
    pulled,
    message: pushed || pulled ? `Synced ${pushed} local and ${pulled} remote change${pushed + pulled === 1 ? '' : 's'}.` : 'Everything is already in sync.'
  };
}

async function hydratePushBatch(events: OutboxEvent[]) {
  const hydrated: PushEvent[] = [];
  let bodyBytes = textEncoder.encode('{"events":[]}').byteLength;
  const skippedPhotoIds: string[] = [];

  for (const event of events) {
    let hydratedEvent: PushEvent;
    if (event.type === 'item.delete') {
      hydratedEvent = event;
    } else {
      const item = await db.items.get(event.itemId);
      if (!item) {
        const current = await db.outbox.get(event.itemId);
        if (current?.id === event.id) await db.outbox.delete(event.itemId);
        continue;
      }
      try {
        const photoBase64 = await blobToBase64(item.photo);
        if (photoBase64.length > MAX_SYNC_PHOTO_BASE64_LENGTH) {
          skippedPhotoIds.push(event.itemId);
          continue;
        }
        hydratedEvent = {
          ...event,
          payload: { ...event.payload, photoBase64, photoMimeType: item.photoMimeType }
        };
      } catch {
        skippedPhotoIds.push(event.itemId);
        continue;
      }
    }

    const eventBytes = textEncoder.encode(JSON.stringify(hydratedEvent)).byteLength + (hydrated.length ? 1 : 0);
    if (bodyBytes + eventBytes > MAX_PUSH_BODY_BYTES) {
      if (hydrated.length) break;
      skippedPhotoIds.push(event.itemId);
      continue;
    }
    hydrated.push(hydratedEvent);
    bodyBytes += eventBytes;
  }

  return { events: hydrated, skippedPhotoIds };
}

async function applyRemoteItems(values: unknown[]) {
  const remoteItems = values.flatMap((value) => {
    const item = normalizeRemoteItem(value);
    return item ? [item] : [];
  });
  const results = await Promise.all(remoteItems.map(async (remote) => {
    const local = await db.items.get(remote.id);
    if (local?.pendingSync) return 0;
    if (local && Date.parse(local.updatedAt) > Date.parse(remote.updatedAt)) return 0;
    if (remote.deletedAt) {
      if (!local) return 0;
      await db.items.delete(remote.id);
      return 1;
    }
    if (!remote.photoBase64 && !local) return 0;

    const { photoBase64, deletedAt: _deletedAt, serverUpdatedAt: _serverUpdatedAt, ...metadata } = remote;
    let photo = local?.photo;
    if (photoBase64) {
      try {
        photo = base64ToBlob(photoBase64, remote.photoMimeType);
      } catch {
        return 0;
      }
    }
    if (!photo) return 0;
    const item: ItemRecord = { ...metadata, photo: await photoForStorage(photo), pendingSync: false };
    await db.items.put(item);
    return 1;
  }));
  return results.reduce<number>((sum, count) => sum + count, 0);
}

async function fetchJson<T extends { error?: string }>(url: string, init: RequestInit, fallback: string) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(url, { ...init, signal: controller.signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw new Error(`${fallback}: the server took too long to respond.`);
    throw error;
  } finally {
    clearTimeout(timeout);
  }

  let json: T;
  try {
    json = await response.json() as T;
  } catch {
    throw new Error(`${fallback}: the server returned invalid JSON.`);
  }
  if (!response.ok) throw new Error(json.error || `${fallback} with ${response.status}.`);
  return json;
}
